import React from "react";
import { Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  SortIcon,
  DropdownDots,
  OnlineSuccess,
  OfflineIcon,
} from "../../../../components/ui/icons";
import SuperAdminDeleteDeviceModal from "./SuperAdminDeleteDeviceModal";

interface IProps {
  onDeleteSuccess: () => void;
}

interface ISuperAdminDeviceActionsProps {
  isConnected: boolean;
  deviceDisplayName: string;
  deviceId: string;
  organizationId: string;
  organizationName: string;
  onDeleteSuccess: () => void;
}

const SuperAdminDeviceActions = ({
  isConnected,
  deviceDisplayName,
  deviceId,
  organizationId,
  organizationName,
  onDeleteSuccess,
}: ISuperAdminDeviceActionsProps) => {
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  return (
    <>
      <div className="actions-btns">
        <Dropdown align="end" className="superadmin-actions-dropdown">
          <Dropdown.Toggle
            id={`superadmin-device-actions-${deviceId}`}
            className="superadmin-actions-toggle"
          >
            <DropdownDots />
          </Dropdown.Toggle>

          <Dropdown.Menu className="superadmin-actions-menu">
            {isConnected ? (
              <Dropdown.Item
                className="superadmin-actions-menu-item"
                onClick={() => setShowDeleteModal(true)}
              >
                Reclaim
              </Dropdown.Item>
            ) : (
              <OverlayTrigger
                placement="left"
                overlay={
                  <Tooltip id={`delete-disabled-tooltip-${deviceId}`}>
                    Only connected devices can be Reclaimed
                  </Tooltip>
                }
              >
                <span className="disabled-delete-wrapper">
                  <Dropdown.Item
                    disabled
                    className="superadmin-actions-menu-item disabled-delete-item"
                  >
                    Reclaim
                  </Dropdown.Item>
                </span>
              </OverlayTrigger>
            )}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <SuperAdminDeleteDeviceModal
        showModel={showDeleteModal}
        handleClose={handleCloseDeleteModal}
        deviceDisplayName={deviceDisplayName}
        deviceId={deviceId}
        selectedOrganizationId={organizationId}
        selectedOrganizationName={organizationName}
        onDeleteSuccess={onDeleteSuccess}
      />
    </>
  );
};

const SuperAdminDeviceColumns = ({ onDeleteSuccess }: IProps) => {
  return React.useMemo(
    () => [
      {
        accessorFn: (row: any) =>
          row?.deviceDisplayName || row?.displayName || "",
        id: "deviceName",
        header: () => (
          <div className="table-header">
            Device Name <SortIcon />
          </div>
        ),
        footer: (props: any) => props.column.id,
        enableColumnFilter: false,
        cell: (row: any) => (
          <div className="table-cell">
            {row.getValue() && row.getValue() !== "undefined"
              ? row.getValue()
              : ""}
          </div>
        ),
      },
      {
        accessorFn: (row: any) => row?.deviceId || row?.id || "",
        id: "deviceId",
        header: () => (
          <div className="table-header">
            Device ID <SortIcon />
          </div>
        ),
        footer: (props: any) => props.column.id,
        enableColumnFilter: false,
        cell: (row: any) => (
          <div className="table-cell">
            {row.getValue() && row.getValue() !== "undefined"
              ? row.getValue()
              : ""}
          </div>
        ),
      },
      {
        accessorKey: "deviceStatus",
        header: () => (
          <div className="table-header">
            Status <SortIcon />
          </div>
        ),
        footer: (props: any) => props.column.id,
        enableColumnFilter: false,
        cell: (row: any) => {
          const statusValue = row.getValue();

          return (
            <div className="table-cell">
              <div className="status-cell">
                <span className="status-icon">
                  {statusValue ? (
                    statusValue === "connected" ? (
                      <OnlineSuccess />
                    ) : (
                      <OfflineIcon />
                    )
                  ) : null}
                </span>

                <span className="status-text">
                  {statusValue
                    ? statusValue === "connected"
                      ? "Online"
                      : "Offline"
                    : ""}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        accessorFn: (row: any) =>
          row?.iotAgentVersion !== undefined &&
          row?.iotAgentVersion !== "undefined"
            ? row?.iotAgentVersion
            : row?.applicationVersion,
        id: "iotAgentVersion",
        header: () => (
          <div className="table-header table-header-stacked">
            <span>IoT Agent / Application Version</span> <SortIcon />
          </div>
  ),
        footer: (props: any) => props.column.id,
        enableColumnFilter: false,
        cell: (row: any) => (
          <div className="table-cell">
            {row.getValue() && row.getValue() !== "undefined"
              ? row.getValue()
              : ""}
          </div>
        ),
      },
      {
        accessorFn: (row: any) =>
          row?.firmwareVersion !== undefined &&
          row?.firmwareVersion !== "undefined"
            ? row?.firmwareVersion
            : row?.androidVersion,
        id: "firmwareVersion",
        header: () => (
          <div className="table-header table-header-stacked">
            <span>Firmware / Android Version</span> <SortIcon />
          </div>
  ),
        footer: (props: any) => props.column.id,
        enableColumnFilter: false,
        cell: (row: any) => (
          <div className="table-cell">
            {row.getValue() && row.getValue() !== "undefined"
              ? row.getValue()
              : ""}
          </div>
        ),
      },
      {
        id: "actions",
        header: () => <span></span>,
        footer: (props: any) => props.column.id,
        enableColumnFilter: false,
        cell: ({ row }: any) => {
          const deviceStatus = row?.original?.deviceStatus;
          const isConnected = deviceStatus === "connected";

          const deviceDisplayName =
            row?.original?.deviceDisplayName ||
            row?.original?.displayName ||
            "";

          const deviceId = row?.original?.deviceId || row?.original?.id || "";

          const organizationId = row?.original?.organizationId || "";
          const organizationName = row?.original?.organizationName || "";

          return (
            <SuperAdminDeviceActions
              isConnected={isConnected}
              deviceDisplayName={deviceDisplayName}
              deviceId={deviceId}
              organizationId={organizationId}
              organizationName={organizationName}
              onDeleteSuccess={onDeleteSuccess}
            />
          );
        },
      },
    ],
    [onDeleteSuccess]
  );
};

export default SuperAdminDeviceColumns;